import {
	Button,
	CardContent,
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect } from 'react'

import Dialog from '@mui/material/Dialog'

import { ReactComponent as CrossCloseLogo } from '../../assets/cross_close_logo.svg'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { fetchComponentsById, fetchRationById } from 'store/rationSystemSlice'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { COLORS } from '../../styles/colors'
import { TypographyHeader } from '../../ui/typography/TypographyHeader'
import { TypographyHeaderSecondary } from '../../ui/typography/TypographyHeaderSecondary'
import { TypographySecondary } from '../../ui/typography/TypographySecondary'
import { roundingNumber } from '../shared/processes'

export interface SimpleDialogProps {
	open: boolean
	title: string
	rationId: number
	onClose: () => void
}

function SimpleDialog(props: SimpleDialogProps) {
	const { onClose, open, title, rationId } = props
	const handleClose = () => {
		onClose()
	}
	const dispatch = useAppDispatch()

	useEffect(() => {
		if (open) {
			dispatch(fetchComponentsById(rationId.toString())).then(() => {
				dispatch(fetchRationById(rationId.toString()))
			})
		}
	}, [open])

	const { rationInfo, rationCompById, loading } = useAppSelector(
		store => store.rationSystemReducer,
	)
	const waterWeight =
		(100 * +rationInfo?.finallyDryMatterKg -
			+rationInfo?.finallyCV * +rationInfo?.finallyWeightMatterKg) /
		+rationInfo?.finallyCV
	return (
		<Dialog
			onClose={handleClose}
			open={open}
			maxWidth='md'
			fullWidth
			scroll='body'
			PaperProps={{
				style: { borderRadius: '12px', height: '500px' },
			}}
		>
			<CardContent>
				<Box
					display='flex'
					flexDirection='row'
					justifyContent='space-between'
					alignItems='center'
				>
					<TypographyHeader sx={{ pl: '8px' }}>{`Состав рациона: ${
						title && title
					}`}</TypographyHeader>
					<IconButton onClick={handleClose}>
						<CrossCloseLogo style={{ stroke: COLORS.secondary }} />
					</IconButton>
				</Box>

				{loading ? (
					<Box
						sx={{
							marginTop: '25%',
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
						}}
					>
						<CircularProgress
							sx={{
								'&.MuiCircularProgress-colorPrimary': { color: COLORS.main },
							}}
						/>
					</Box>
				) : (
					<>
						<TypographySecondary14 sx={{ pt: '8px', pl: '8px' }}>
							Сухое вещество:{' '}
							<span style={{ color: COLORS.black }}>
								{roundingNumber(rationInfo?.dry_matter_total, 2) + '%'}
							</span>
						</TypographySecondary14>
						<TypographySecondary14 sx={{ pl: '8px', marginTop: '16px' }}>
							Влажность рациона:{' '}
							<span style={{ color: COLORS.black }}>
								{roundingNumber(rationInfo?.food_humidity, 2)}
							</span>{' '}
							%
						</TypographySecondary14>

						<TableContainer sx={{ pt: '24px', ml: '-8px' }}>
							<Table
								sx={{
									ml: '18px',
									width: '97%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
									},
								}}
							>
								<TableHead>
									<TableRow>
										<TableCell
											sx={{
												fontSize: '12px',
												lineHeight: '15px',
												color: COLORS.icon,
												borderBottom: `1px solid ${COLORS.secondaryFontOpacity} !important`,
												textAlign: 'left',
												pl: '0px',
											}}
										>
											Компоненты
										</TableCell>
										<TableCell
											sx={{
												fontSize: '12px',
												lineHeight: '15px',
												color: COLORS.icon,
												borderBottom: `1px solid ${COLORS.secondaryFontOpacity} !important`,
											}}
											align='right'
										>
											Вес кг
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									<TableRow>
										<TableCell
											sx={{
												fontSize: '12px',
												lineHeight: '15px',
												color: COLORS.black,
												pl: '0px',
											}}
										>
											Вода
										</TableCell>
										<TableCell
											sx={{
												fontSize: '12px',
												lineHeight: '15px',
												color: COLORS.black,
											}}
											align='right'
										>
											{waterWeight >= 0 ? roundingNumber(waterWeight, 2) : 0}{' '}
											<span style={{ color: COLORS.icon }}>кг</span>
										</TableCell>
									</TableRow>
									{rationInfo?.components.map(component => (
										<TableRow key={component.component_id}>
											<TableCell
												sx={{
													fontSize: '12px',
													lineHeight: '15px',
													color: COLORS.black,
													pl: '0px',
												}}
											>
												{
													rationCompById?.find(
														el => el.id === component.component_id,
													)?.title
												}
											</TableCell>
											<TableCell
												sx={{
													fontSize: '12px',
													lineHeight: '15px',
													color: COLORS.black,
												}}
												align='right'
											>
												{component.head_weight}{' '}
												<span style={{ color: COLORS.icon }}>кг</span>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</>
				)}
			</CardContent>
		</Dialog>
	)
}

export default function PlanCardCompound(props) {
	const [open, setOpen] = React.useState(false)

	const handleClickOpen = () => {
		setOpen(true)
	}

	const handleClose = () => {
		setOpen(false)
	}
	if (props.ration === null) return <></>

	return (
		<Box>
			<Box
				display='flex'
				flexDirection='row'
				alignItems='center'
				sx={{ pt: '24px' }}
			>
				<TypographyHeaderSecondary sx={{ pr: '8px' }}>
					Рацион: {props.ration === null ? 'Рацион не указан' : props.ration}
				</TypographyHeaderSecondary>
				<Button
					onClick={handleClickOpen}
					sx={{
						textTransform: 'none',
						borderRadius: '0px',
						height: '15px',
						pl: '0px',
						textDecoration: 'underline',
						color: COLORS.black,
						'&:hover': { backgroundColor: 'transparent' },
					}}
				>
					<TypographySecondary
						sx={{ color: COLORS.main, textDecoration: 'underline' }}
					>
						Состав
					</TypographySecondary>
				</Button>
			</Box>
			<SimpleDialog
				open={open}
				onClose={handleClose}
				rationId={props.rationId}
				title={props.ration}
			/>
		</Box>
	)
}
